// @flow

import {
  ACCEPT_INVITE,
  ACCEPT_TERMS,
  CONFIRMATION_CODE,
  DISMISS_ERROR,
  FETCH_FACILITY_DETAILS,
  LOG_IN,
  LOG_IN_MFA,
  LOG_OUT,
  REJECT_WITHINGS_INTEGRATION,
  RESET_LOADING_USER,
  SET_CREDENTIALS,
  SET_USER_NAME,
  SET_WEBEX_FOR_STAFF,
  UPDATE_AVAILABILITY_STATUS,
} from "./actionTypes";
import storageService from "../../services/storageService";
import { STORAGE_TYPE, store, persistor } from "../store/configureStore";
import userService from "../../services/userService";
import globalCacheService from "../../services/globalCacheService";
import recentPatientsStorageService from "../../services/recentPatientsStorageService";
import onboardingService from "../../services/onboardingService";
import assigneeCacheService from "../../services/assigneeCacheService";
import chatSocketService from "../../services/chatSocketService";
import { ONBOARDING_LOCALE, PREFERRED_LOCALE } from "../../services/userService";

export function onResetLoading() {
  return { type: RESET_LOADING_USER };
}

export function onAcceptInvite(
  inviteCode,
  email,
  phone,
  challengeFields,
  preValidationRequired
) {
  return {
    type: ACCEPT_INVITE,
    inviteCode,
    email,
    phone,
    challengeFields,
    preValidationRequired,
  };
}
export function onAcceptTerms() {
  return { type: ACCEPT_TERMS };
}
export function onConfirmationCode(confirmationCode, inviteCode) {
  return { type: CONFIRMATION_CODE, confirmationCode, inviteCode };
}
export function onSetCredentials(loginUserId, password) {
  return { type: SET_CREDENTIALS, loginUserId, password };
}

export function onDismissError() {
  // console.log('DISMISS ERROR');
  return { type: DISMISS_ERROR };
}

export function onLogin(username, password, redirect, signup) {
  // console.log('actions onLogin', username, password);
  return { type: LOG_IN, username, password, redirect, signup };
}

export function onLoginWithMfa(code, mfaToken, mfaMechanism) {
  // console.log('actions onLogin', username, password);
  return {
    type: LOG_IN_MFA,
    code,
    mfaToken,
    mfaMechanism,
  };
}

export function onLogout(message, skipAggressiveClear = false) {
  let loggedIn = store.getState().user.loggedIn;

  if (loggedIn) {
    userService.doLogout();
  }

  // Explicitly disconnect the chat Socket.io singleton so the analytics
  // server's presence tracker can flip this user to offline immediately.
  // Without this, the server waits for the ping timeout (up to ~85 s) to
  // detect the dead socket — during which the partner still sees the
  // green "online" dot.
  try {
    chatSocketService.disconnect();
  } catch (_) { /* never block logout */ }

  // Skip aggressive clearing if called from signup or if not logged in
  if (skipAggressiveClear || !loggedIn) {
    return { type: LOG_OUT, message };
  }

  // Aggressively clear all persistence - both redux and manual
  try {
    if (persistor) {
      // Purge all persisted state
      persistor.purge().then(() => {
        console.log("Redux persistence purged successfully");
      });
    }
  } catch (e) {
    console.warn("Error purging Redux persistence:", e);
  }

  // Immediately and aggressively clear all storage
  storageService.clearSessionStorage();

  // Manual brute force clearing of persist keys
  try {
    const persistKeys = Object.keys(sessionStorage).filter((key) =>
      key.startsWith("persist:")
    );
    persistKeys.forEach((key) => {
      sessionStorage.removeItem(key);
      console.log("Manually removed persist key:", key);
    });

    const localPersistKeys = Object.keys(localStorage).filter((key) =>
      key.startsWith("persist:")
    );
    localPersistKeys.forEach((key) => {
      localStorage.removeItem(key);
      console.log("Manually removed local persist key:", key);
    });
  } catch (e) {
    console.warn("Error manually clearing persist keys:", e);
  }

  // Also explicitly clear the preferred locale key
  try {
    storageService.removeFromSessionStorage(PREFERRED_LOCALE);
    console.log("Cleared PREFERRED_LOCALE from sessionStorage");
  } catch (e) {
    console.warn("Error clearing PREFERRED_LOCALE:", e);
  }

  // SECURITY FIX: Clear all localStorage to prevent data leakage between users
  // BUT preserve onboarding/tooltip data which should persist per user
  const onboardingKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith("userSeenFeatures_")
  );
  const onboardingData = {};
  onboardingKeys.forEach((key) => {
    onboardingData[key] = localStorage.getItem(key);
  });

  // Preserve task dashboard filters (staff-scoped)
  const taskFilterKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith("taskDashboardFilters_")
  );
  const taskFilterData = {};
  taskFilterKeys.forEach((key) => {
    taskFilterData[key] = localStorage.getItem(key);
  });

  // Preserve preferredLocale
  const preferredLocale = localStorage.getItem('preferredLocale');

  // Preserve user preferences (staff-scoped, e.g. landing page, notes expanded)
  const userPrefsKeys = Object.keys(localStorage).filter((key) =>
    key.startsWith("userPreferences_")
  );
  const userPrefsData = {};
  userPrefsKeys.forEach((key) => {
    userPrefsData[key] = localStorage.getItem(key);
  });

  storageService.clearLocalStorage();

  // Restore onboarding data after clearing
  Object.keys(onboardingData).forEach((key) => {
    localStorage.setItem(key, onboardingData[key]);
  });

  // Restore task dashboard filters after clearing
  Object.keys(taskFilterData).forEach((key) => {
    localStorage.setItem(key, taskFilterData[key]);
  });

  // Restore user preferences after clearing
  Object.keys(userPrefsData).forEach((key) => {
    localStorage.setItem(key, userPrefsData[key]);
  });

  // Restore preferredLocale
  if (preferredLocale) {
    localStorage.setItem('preferredLocale', preferredLocale);
  }

  // SECURITY FIX: Clear all global caches to prevent data leakage between users
  globalCacheService.clearAllCache();

  // Clear user profile cache to prevent cross-user data leakage
  userService.clearProfileCache();
  // Clear assignee cache (IndexedDB + in-memory)
  assigneeCacheService.clearCache();
  // Clear user profile cache to prevent cross-user data leakage
  userService.clearProfileCache();

  // NOTE: We don't clear recent patients on logout anymore since they are now
  // properly isolated by staffId in IndexedDB. Each staff member keeps their own recent patients.

  // Clear global staff cache
  if (window.MYP_GLOBAL_STAFF_CACHE) {
    window.MYP_GLOBAL_STAFF_CACHE.data = null;
    window.MYP_GLOBAL_STAFF_CACHE.staffOptions = null;
    window.MYP_GLOBAL_STAFF_CACHE.isLoading = false;
    window.MYP_GLOBAL_STAFF_CACHE.callbacks = [];
  }

  // Clear any other potential window-level caches
  if (window.MYP_API_CACHE) {
    window.MYP_API_CACHE = {};
  }

  // Reset onboarding service to prevent cross-user data leakage
  try {
    onboardingService.userSeenFeatures.clear();
    onboardingService.pendingAnnouncements = [];
    onboardingService.isInitialized = false;
    onboardingService.userId = null;
  } catch (e) {
    console.warn("Error clearing onboarding service:", e);
  }

  return { type: LOG_OUT, message };
}

export function onFetchFacilityDetails() {
  return { type: FETCH_FACILITY_DETAILS };
}

export function onUpdateAvailabilityStatus(availabilityStatus) {
  return { type: UPDATE_AVAILABILITY_STATUS, availabilityStatus };
}

export function onSetOnboardingUserName(userName) {
  return { type: SET_USER_NAME, userName };
}

export function onRejectWithingsIntegration() {
  return {
    type: REJECT_WITHINGS_INTEGRATION,
  };
}

export function setWebex(webex) {
  return {
    type: SET_WEBEX_FOR_STAFF,
    webex,
  };
}

// export function onSwitchUserType(userType, organizationId) {
//   // console.log(' USER TYPE', userType, organizationId);
//   return { type: SWITCH_USER_TYPE, userType, organizationId };
// }
